import React, { useEffect, useState } from "react";
import {
	Stepper,
	Step,
	StepContent,
	Icon,
	IconButton,
	StepButton,
	Tooltip,
	StepLabel,
} from "../../../common/MaterialComponent";
import "../IVRFlowConfiguration/IVRFlowConfiguration.css";
import { ConfirmationDialog } from "../../Confirmation/ConfirmationDialog";
import {
	WizardStepperProp,
	IVRFlowStepContent,
} from "../../../hooks/useIVRFlowWidgetConfiguration";
import { useGetComponentStringsContext } from "../../../hooks/useGetComponentStrings";

interface IVRFlowWizardStepperProps {
	stepperContent: IVRFlowStepContent[];
	widgetIdToBeValidated: string;
	handleDeleteWidget: (widgetId: string) => void;
	activeStepperId: string;
	setActiveStepperId: (widgetId: string) => void;
}

export const IVRFlowWizardStepper: React.FC<IVRFlowWizardStepperProps> = ({
	stepperContent,
	handleDeleteWidget,
	widgetIdToBeValidated,
	activeStepperId,
	setActiveStepperId,
}) => {
	const [activeStep, setActiveStep] = React.useState(0);
	const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
	const [widgetId, setWidgetId] = useState("");
	const [stepperCount, setStepperCount] = useState(stepperContent.length);
	const [highlightRequiredDetail, setHighlightRequiredDetail] = useState(false);
	const strings = useGetComponentStringsContext().IVRFlowWizardStepper;

	const handleStep = (step: number, id: string) => () => {
		setActiveStepperId(step === activeStep ? "" : id);
		setActiveStep(step === activeStep ? -1 : step);
	};

	const onDeleteWidget = (widgetId: string) => {
		setWidgetId(widgetId);
		setOpenConfirmationDialog(true);
	};

	const handleDeleteWidgetDetail = async (isDeleteRecord: boolean = false) => {
		if (isDeleteRecord) {
			handleDeleteWidget(widgetId);
		}
		setOpenConfirmationDialog(false);
		setWidgetId("");
	};

	// update value of activeStep, when new widget is added in stepperContent list
	useEffect(() => {
		if (stepperContent.length > stepperCount) {
			setActiveStep(stepperContent.length - 1);
		}
		setStepperCount(stepperContent.length);
	}, [stepperContent.length, stepperCount]);

	useEffect(() => {
		if (widgetIdToBeValidated) {
			const index = stepperContent.findIndex(
				(s) => s.id === widgetIdToBeValidated
			);
			if (index > -1) {
				setActiveStep(index);
				setHighlightRequiredDetail(true);
			}
		}
	}, [stepperContent, widgetIdToBeValidated]);

	useEffect(() => {
		if (activeStepperId) {
			const index = stepperContent.findIndex((x) => x.id === activeStepperId);
			setActiveStep(index);
		}
		// only update when activeStepperId props value changed
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activeStepperId]);

	return (
		<div className="ivrFlowStepper">
			<Stepper nonLinear activeStep={activeStep} orientation="vertical">
				{stepperContent.map((content, index) => {
					content = {
						...content,
						componentProps: {
							...content.componentProps,
							highlightRequiredDetail,
						},
					};

					let Component: React.FC<WizardStepperProp> = content.component;

					return (
						<Step key={content.id}>
							<StepLabel>
								<StepButton
									onClick={handleStep(index, content.id)}
									className={
										"stepBtnContent " +
										(activeStep !== index ? "" : "stepBtnActive")
									}
								>
									{content.title}
								</StepButton>
								<Tooltip title={strings.deleteWidgetTooltip}>
									<IconButton onClick={() => onDeleteWidget(content.id)}>
										<Icon color="primary">delete</Icon>
									</IconButton>
								</Tooltip>
							</StepLabel>

							<StepContent>
								<Component
									key={content.id}
									{...(content.componentProps as WizardStepperProp)}
								/>
							</StepContent>
						</Step>
					);
				})}
			</Stepper>
			<ConfirmationDialog
				open={openConfirmationDialog}
				message={strings.widgetDeleteConfirmationMessage}
				closeDialog={(isDeleteRecord?: boolean) =>
					handleDeleteWidgetDetail(isDeleteRecord)
				}
				positiveActionText="Yes"
				negativeActionText="No"
			></ConfirmationDialog>
		</div>
	);
};
